import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useMutation} from '@github-ui/react-query'
import {
  useMarkersActions,
  useGetThread,
  usePendingReviewActions,
  usePullRequestBasePath,
  useOnCommentThreadDeleted,
} from '../stores/layout-selectors'
import type {FileKey, LineKey} from '@github-ui/diff-lines/types'

export function useDeleteReviewCommentMutation({
  filePath,
  lineMarkersKey,
  onSuccess,
}: {
  filePath: string
  lineMarkersKey: LineKey | FileKey
  onSuccess?: () => void
}) {
  const basePath = usePullRequestBasePath()
  const {deleteThreadComment} = useMarkersActions()
  const onCommentThreadDeleted = useOnCommentThreadDeleted()
  const getThread = useGetThread()
  const {removePendingComment} = usePendingReviewActions()

  return useMutation({
    mutationFn: async ({commentId, threadId}: {commentId: string; threadId: string; filePath: string}) => {
      const comment = getThread(Number(threadId), filePath, lineMarkersKey)?.commentsData?.comments?.find(
        c => c.id === commentId,
      )
      if (!comment) return

      const apiUrl = `${basePath}/page_data/review_comments/${comment.databaseId}`
      const response = await fetchWithErrorHandling(`${apiUrl}`, {
        method: 'DELETE',
      })

      if (response.status === 204 || response.status === 200) {
        return
      }

      // If we don't return a 204 (no content), we need to parse the response
      // and render the errors
      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
    },

    onSuccess: (_, {commentId, threadId}) => {
      const result = deleteThreadComment(Number(threadId), commentId, filePath, lineMarkersKey)

      removePendingComment(Number(threadId))

      if (result !== 'thread_deleted') {
        return
      }

      if (result === 'thread_deleted') {
        onCommentThreadDeleted?.({
          path: filePath,
          threadID: threadId,
        })
      }

      onSuccess?.()
    },
  })
}
